import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

export default function RtoAgentShow() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [agent, setAgent] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAgent();
    }, [id]);

    const loadAgent = async () => {
        try {
            const response = await api.get(`/rto_agents/${id}`);
            setAgent(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading RTO agent:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Are you sure you want to delete this RTO agent?')) return;

        try {
            await api.delete(`/rto_agents/${id}`);
            navigate('/rto_agents');
        } catch (error) {
            alert('Error deleting agent. It may be in use.');
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!agent) return <div className="p-8">RTO agent not found.</div>;

    return (
        <div className="p-8 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">{agent.agent_name}</h1>
                    <p className="text-slate-600 mt-1">RTO Agent Details</p>
                </div>
                <div className="flex gap-3">
                    <Link
                        to={`/rto_agents/${id}/edit`}
                        className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all"
                    >
                        Edit
                    </Link>
                    <button
                        onClick={handleDelete}
                        className="px-6 py-3 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-all"
                    >
                        Delete
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Agent Name</label>
                        <p className="text-lg font-bold text-slate-900">{agent.agent_name}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Service Area</label>
                        <p className="text-lg text-slate-700">{agent.service_area || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Phone</label>
                        <p className="text-lg text-slate-700">{agent.phone || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Email</label>
                        <p className="text-lg text-slate-700">{agent.email || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Commission Rate</label>
                        <p className="text-lg text-slate-700">{agent.commission_rate ? `${agent.commission_rate}%` : '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Status</label>
                        <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${agent.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {agent.is_active ? 'Active' : 'Inactive'}
                        </span>
                    </div>

                    {agent.address && (
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Address</label>
                            <p className="text-lg text-slate-700 whitespace-pre-wrap">{agent.address}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-8">
                <Link
                    to="/rto_agents"
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to RTO Agents
                </Link>
            </div>
        </div>
    );
}
